import { useApolloClient } from '@apollo/client';
import { Button, Justify, Row } from '@busybusy/webapp-react-ui';
import { EQUIPMENT_IDS_QUERY } from 'apollo/queries/equipment-queries';
import { EquipmentIcon } from 'assets/icons';
import classNames from 'classnames';
import { HeaderDialog, Panel } from 'components';
import { IDashboardSettingsItem } from 'components/domain/dashboard/DashboardSettingsItem/DashboardSettingsItem';
import DashboardContentCard from 'components/foundation/dashboard/DashboardContentCard/DashboardContentCard';
import DashboardEnableCard from 'components/foundation/dashboard/DashboardEnableCard/DashboardEnableCard';
import DashboardActiveList from 'components/foundation/dashboard/dashboard-list/DashboardActiveList/DashboardActiveList';
import { IDashboardListRow } from 'components/foundation/dashboard/dashboard-list/DashboardList/DashboardList';
import { DashboardTheme } from 'containers/dashboard/types/types';
import { getDashboardTimeFrameTitle } from 'containers/dashboard/util/utils';
import { useEquipmentManagementListNavigation, useOpenable } from 'hooks';
import useOrganizationUpdate from 'hooks/models/organization/useOrganizationUpdate';
import useActivePosition from 'hooks/models/position/useActivePosition';
import useEquipmentDashboardNavigation from 'hooks/navigation/useEquipmentDashboardNavigation';
import useEquipmentEnabled from 'hooks/settings/useEquipmentEnabled/useEquipmentEnabled';
import { isEmpty } from 'lodash';
import { useEffect, useState } from 'react';
import IEquipment from 'types/Equipment';
import { colors } from 'utils/constants/colors';
import { t } from 'utils/localize';
import DashboardCardSpinner from '../../DashboardCardSpinner/DashboardCardSpinner';
import DashboardErrorCard from '../../DashboardErrorCard/DashboardErrorCard';
import { defaultActiveEquipmentDashboardCardTimeFrame } from '../constants/constants';

export interface IActiveEquipmentDashboardCardContentProps {
  data: IDashboardListRow[];
  loading: boolean;
  onReload: () => void;
  onDetailClick: () => void;
  onHide: () => void;
  theme: DashboardTheme;
  error: string | null;
  setting: IDashboardSettingsItem;
  className?: string | null;
}

const ActiveEquipmentDashboardCardContent = ({
  data,
  loading,
  error,
  onReload,
  onDetailClick,
  setting,
  theme,
  onHide,
  className,
}: IActiveEquipmentDashboardCardContentProps) => {
  const navigateToEquipmentManagement = useEquipmentManagementListNavigation();
  const navigateToEquipmentDashboard = useEquipmentDashboardNavigation();
  const title = getDashboardTimeFrameTitle(
    setting.title,
    setting.options?.time ?? defaultActiveEquipmentDashboardCardTimeFrame
  );

  const activePosition = useActivePosition();

  const settingsDialogDetails = useOpenable();

  const [updateKey] = useOrganizationUpdate();
  const [shouldShowCreateEquipment, setShouldShowCreateEquipment] = useState(false);
  const client = useApolloClient();
  const enabled = useEquipmentEnabled();

  useEffect(() => {
    if (enabled) {
      client
        .query<{ equipment: IEquipment[] }>({
          query: EQUIPMENT_IDS_QUERY,
          variables: {
            first: 1,
          },
        })
        .then((results) => {
          setShouldShowCreateEquipment(isEmpty(results.data.equipment));
        });
    }
  }, [client, enabled]);

  function openEquipmentSettings() {
    updateKey('trackEquipment', true);
    settingsDialogDetails.open();
  }

  function openEquipmentManagement() {
    navigateToEquipmentManagement();
  }

  function openEquipmentDashboard(equipmentId: string) {
    navigateToEquipmentDashboard({ equipment_id: equipmentId });
  }

  function renderList() {
    return data && data?.length > 0 ? (
      <DashboardActiveList rowItems={data} onRowClick={openEquipmentDashboard} theme={theme} />
    ) : null;
  }

  const classes = classNames('active-equipment-dashboard-card', className);

  return (
    <>
      {enabled ? (
        !error ? (
          !shouldShowCreateEquipment || loading ? (
            <DashboardContentCard
              tabColor={colors.primary}
              subheaderValue={title}
              className={classes}
              theme={theme}
              rightLabel={t('View Report')}
              onRightLabelClick={onDetailClick}
            >
              {loading ? <DashboardCardSpinner /> : renderList()}
            </DashboardContentCard>
          ) : (
            <DashboardEnableCard
              title={setting.title}
              svg={EquipmentIcon}
              buttonLabel={t('Create Equipment')}
              onClick={openEquipmentManagement}
              theme={theme}
              onHide={onHide}
            />
          )
        ) : (
          <DashboardErrorCard theme={theme} onClick={onReload} />
        )
      ) : activePosition?.manageCompanySettings ? (
        <DashboardEnableCard
          title={setting.title}
          svg={EquipmentIcon}
          buttonLabel={t('Enable Equipment')}
          onClick={openEquipmentSettings}
          theme={theme}
          onHide={onHide}
        />
      ) : null}
      <HeaderDialog
        title={t('Equipment Enabled')}
        isOpen={settingsDialogDetails.isOpen}
        onClose={settingsDialogDetails.close}
        position="center"
      >
        <Panel className="px-8 py-6">
          <div>
            {t(
              'Would you like to visit the equipment management page where you can create and edit equipment for your company?'
            )}
          </div>
          <Row justify={Justify.FLEX_END}>
            <Button type="secondary" onClick={settingsDialogDetails.close} className="mr-4">
              {t('No Thanks')}
            </Button>
            <Button type="primary" onClick={openEquipmentManagement}>
              {t('Manage')}
            </Button>
          </Row>
        </Panel>
      </HeaderDialog>
    </>
  );
};

export default ActiveEquipmentDashboardCardContent;
